import { Box, SxProps } from '@mui/material';
import { DARK, GRAY } from 'constants/colors';
import { TO_PLAN_DRAWER_WIDTH } from 'constants/widths';
import { format } from 'date-fns';
import { createRef, useEffect } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import { formatByLocale } from 'utils/date';
import { ZoomLevel } from './TimelineBar';

interface TimeWindowProps {
  time: Date;
  previousTime?: Date;
  nextTime?: Date;
  x: number;
  dateOffset: number;
  width: number;
  toPlanDrawerIsOpen?: boolean;
  zoomLevel: ZoomLevel;
  onChangeCurrentDay?: (day: Date) => void;
}

const DATE_TRIGGER_CLASS = '__DATE_TRIGGER_CLASS__';

export const DayFlag = ({
  time,
  zoomLevel,
  dateOffset,
  sx
}: {
  time: Date | undefined | null;
  zoomLevel: ZoomLevel;
  dateOffset: number;
  sx?: SxProps;
}) => {
  if (!time) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '-16px',
        left: 4 - dateOffset + 'px',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        minWidth: '100px',
        height: '16px',
        fontSize: '11px',
        whiteSpace: 'nowrap',
        ...(sx || {})
      }}
    >
      {zoomLevel == ZoomLevel.XXXXS
        ? formatByLocale(time, 'E d MMM').replace('.', '')
        : formatByLocale(time, 'EEEE d MMMM')}
    </Box>
  );
};

export const TimeWindow = (props: TimeWindowProps) => {
  const {
    x,
    time,
    previousTime,
    nextTime,
    width,
    toPlanDrawerIsOpen,
    zoomLevel,
    dateOffset,
    onChangeCurrentDay
  } = props;
  const formattedTime = format(time, 'HH:mm');

  const ref = createRef<HTMLDivElement>();
  const toPlanDrawerPercentage =
    (100 / window.innerWidth) * TO_PLAN_DRAWER_WIDTH;
  // TODO: replace with vanilla JS version due to deprecation
  const entry = useIntersectionObserver(ref, {
    rootMargin: `0% -${
      toPlanDrawerIsOpen ? 50 + toPlanDrawerPercentage / 2 : 50
    }% 0% 0%`
  });
  const isTriggerTimePeriod =
    entry?.target.classList.contains(DATE_TRIGGER_CLASS);
  const { isIntersecting } =
    !!entry && isTriggerTimePeriod ? entry : { isIntersecting: false };

  const isDifferentDay = previousTime?.getDay() !== time.getDay();

  useEffect(() => {
    const { left } = entry?.boundingClientRect || { left: 0 };
    if (isTriggerTimePeriod && left > 0 && left < window.innerWidth)
      onChangeCurrentDay?.(time);
  }, [isIntersecting]);

  return (
    <Box
      ref={ref}
      className={
        previousTime?.getDay() !== nextTime?.getDay() ? DATE_TRIGGER_CLASS : ''
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        position: 'absolute',
        top: '8px',
        zIndex: 40,
        left: `${x}px`,
        width: `${width}px`,
        borderLeft: `2px solid ${GRAY}`,
        height: '18px',
        fontSize: '11px',
        color: DARK,
        opacity: 0.8
      }}
    >
      {isDifferentDay && (
        <DayFlag time={time} zoomLevel={zoomLevel} dateOffset={dateOffset} />
      )}

      <Box pl="4px">{formattedTime}</Box>
    </Box>
  );
};
